<template>
  <div id="HomePage">
    <!-- Loader della dashboard finché has-assets/colocation non risponde: le card nascono già nella forma finale -->
    <div v-if="!colocationChecked" class="homePageLoading">
      <MyLoading :viewLoader="false" type="" :checkOrders="true" />
    </div>
    <div v-else class="mdl-grid contentHomePage">
      <DismissibleBanner
        class="mdl-cell--12-col"
        cookie-key="home-welcome-banner"
        :cookie-days="7"
        variant="promo"
      >
        <span v-html="$t('banner.homePage', { name: user.first_name })" />
      </DismissibleBanner>
      <div
        class="mdl-cell mdl-cell--9-col mdl-cell--12-col-tablet mdl-cell--12-col-phone first"
      >
        <MyHeaderPage
          :title="$t('homePage.title')"
          :subtitle="$t('homePage.subTitle')"
          :calendar="true"
        />
        <div
          class="mdl-grid"
          :class="{ 'hp-no-docs': !hasDocumentPermission }"
          style="justify-content: space-between"
        >
          <div
            class="mdl-cell mdl-cell--6-col mdl-cell--12-col-phone leftColumn noMargin"
          >
            <HomeTicketList
              :tickets="openTickets"
              :loading="ticketsLoaded"
              :has-error="ticketsError"
            />
            <HomeDocumentList
              v-show="hasDocumentPermission"
              :documents="recentDocuments"
              :loading="documentsLoaded"
              :has-error="documentsError"
              :downloading-id="downloadingId"
              @download="downloadDocument"
            />
          </div>
          <div class="mdl-cell--6-col mdl-cell--12-col-phone rightColumn">
            <div class="hpCta">
              <div class="dcCrmRow">
                <StandardCard
                  id="hp-dc-access"
                  v-show="isColocation"
                  custom-class="singleCard"
                >
                  <div class="mdl-grid contentCrm">
                    <div class="mdl-cell--12-col contentCtaCrm text line-clamp-2">
                      {{ $t("homePage.cardDC") }}
                    </div>
                    <div class="contentButton">
                      <ActionButton
                        id="accessDCbtn"
                        custom-class="cdlButton"
                        :title="$t('homePage.btnDC')"
                        @click="router.push($t('nav.accessDcUrl'))"
                      />
                    </div>
                  </div>
                </StandardCard>
                <StandardCard id="hp-crm" custom-class="singleCard">
                  <div class="mdl-grid contentCrm">
                    <div class="mdl-cell--12-col contentCtaCrm text line-clamp-2">
                      {{ $t("homePage.textCrm") }}
                    </div>
                    <div class="contentButton">
                      <ActionButton
                        id="buttonNewSolutionHomePage"
                        custom-class="cdlButton"
                        :title="$t('homePage.ctaCrm')"
                        @click="openCrmModal()"
                      />
                    </div>
                  </div>
                </StandardCard>
              </div>
            </div>
            <HomeIncidentLinks :links="incidentLinks" />
          </div>
        </div>
      </div>
      <div class="mdl-cell mdl-cell--3-col second">
        <HomeMagazineSection :articles="magazines" />
        <router-link to="/fonts" v-if="isDebug">
          <div class="btnFonts">FONTS</div>
        </router-link>
        <p class="hp-version">[2.15.1]</p>
      </div>
    </div>
  </div>
  <HomeCrmModal />
  <StatusAlert v-if="crmSent" status="success" role="alert">
    <template #body>
      <p v-html="$t('homePage.sendSuccess')"></p>
    </template>
  </StatusAlert>
</template>

<script setup>
import { computed, onMounted, watch } from "vue";
import { useRouter } from "vue-router";
import { useI18n } from "vue-i18n";
import { useUserStore } from "@/stores/UserStore";
import { ctrlPermissions } from "@/services/Permissions";
import { useCrmRequest } from "@/composables/dashboard/useCrmRequest";
import StatusAlert from "@/components/Primitives/Alerts/StatusAlert.vue";
import { useHomeTickets } from "@/composables/dashboard/useHomeTickets";
import { useHomeMagazines } from "@/composables/dashboard/useHomeMagazines";
import { useHomeDocuments } from "@/composables/dashboard/useHomeDocuments";

import DismissibleBanner from "@/components/Primitives/Banners/DismissibleBanner.vue";
import StandardCard from "@/components/Primitives/Cards/StandardCard.vue";
import ActionButton from "@/components/Primitives/Buttons/ActionButton.vue";
import MyHeaderPage from "@/components/MyHeaderPage.vue";
import MyLoading from "@/components/MyLoading.vue";
import HomeTicketList from "@/components/Feature/Home/HomeTicketList.vue";
import HomeDocumentList from "@/components/Feature/Home/HomeDocumentList.vue";
import HomeMagazineSection from "@/components/Feature/Home/HomeMagazineSection.vue";
import HomeCrmModal from "@/components/Feature/Home/HomeCrmModal.vue";
import HomeIncidentLinks from "@/components/Feature/Home/HomeIncidentLinks.vue";
import "@/assets/scss/HomePage.scss";

const router = useRouter();
const { t } = useI18n();

// Store
const user = computed(() => useUserStore().user);
const isColocation = computed(() => useUserStore().user.isColocation);

// Permessi
const hasDocumentPermission = computed(() => ctrlPermissions(["document_archive"]));

// Debug flag
const isDebug = window.VUE_APP_DEBUG ? window.VUE_APP_DEBUG === "true" : false;

// Composables
const { openCrmModal, crmSent } = useCrmRequest();
// true quando UserStore.setIfColocation ha ricevuto la risposta (anche in errore)
const colocationChecked = computed(() => user.value?.isColocationChecked === true);
const { openTickets, loaded: ticketsLoaded, hasError: ticketsError, loadOpenTickets } = useHomeTickets();
const { magazines, loadMagazines } = useHomeMagazines();
const {
  recentDocuments,
  loaded: documentsLoaded,
  hasError: documentsError,
  downloadingId,
  loadRecentDocuments,
  downloadDocument,
} = useHomeDocuments();

// Incident links calcolati come computed per reattività i18n
const incidentLinks = computed(() => [
  {
    label: t("homePage.incidentConnectivity"),
    to: "/" + t("nav.listLinesUrl"),
  },
  {
    label: t("homePage.incidentColocation"),
    to: "/" + t("nav.colocationUrl"),
  },
  {
    label: t("homePage.incidentCrossConnect"),
    to: "/" + t("nav.crossConnectsActiveUrl"),
  },
  {
    label: t("homePage.incidentCloud"),
    to: "/cloud",
  },
  {
    label: t("homePage.incidentVoip"),
    to: "/voip",
  },
]);

watch(hasDocumentPermission, (canSee) => {
  if (canSee) loadRecentDocuments();
}, { immediate: true });

onMounted(() => {
  window.scrollTo(0, 0);
  loadOpenTickets();
  loadMagazines();

  // Rimuove eventuali voci di menù duplicate attive
  const listActiveMenu = document.getElementsByClassName("cdlMenu");
  let count = 0;
  Object.entries(listActiveMenu).forEach((elm) => {
    if (
      elm[1].classList.contains("router-link-active") ||
      elm[1].classList.contains("active")
    ) {
      if (count > 0) {
        elm[1].classList.remove("router-link-active");
        elm[1].classList.remove("active");
      }
      count++;
    }
  });
});
</script>

<style>
#accessDCbtn {
  color: white;
  border-radius: 41px;
  padding: 20px;
  text-align: center;
  font-family: OpenSansRegular !important;
  border: 0;
  cursor: pointer;
  font-size: 1.2rem;
}
</style>